import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Calendar, Clock, Share2, Facebook } from 'lucide-react';
import { POSTS } from '../data/site';
import { useEnquiry } from '../context/EnquiryContext';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

const ShareButtons = ({ post }) => {
  const url = `${window.location.origin}/blog/${post.slug}`;
  const text = `${post.title} — Prakruti Homes`;
  return (
    <div className="flex items-center gap-3" data-testid="share-buttons">
      <span className="text-[10px] tracking-[0.24em] text-ink/40 font-medium flex items-center gap-2"><Share2 size={12} /> SHARE</span>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
        target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp"
        data-testid="share-whatsapp-btn"
        className="w-10 h-10 border border-clay flex items-center justify-center text-ink/60 hover:bg-[#25D366] hover:border-[#25D366] hover:text-white transition-colors duration-300"
      >
        <WhatsAppIcon />
      </a>
      <a
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}
        target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook"
        data-testid="share-facebook-btn"
        className="w-10 h-10 border border-clay flex items-center justify-center text-ink/60 hover:bg-[#1877F2] hover:border-[#1877F2] hover:text-white transition-colors duration-300"
      >
        <Facebook size={15} />
      </a>
    </div>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const { open } = useEnquiry();
  const post = POSTS.find((p) => p.slug === slug);
  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  if (!post) {
    return (
      <main className="pt-40 pb-32 min-h-screen" data-testid="post-not-found">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-heading font-extrabold text-4xl tracking-tighter text-ink">Article not found</h1>
          <Link to="/blog" data-testid="post-back-btn" className="mt-6 inline-flex items-center gap-2 text-terra text-[11px] tracking-[0.2em] font-medium">
            <ArrowLeft size={14} /> ALL ARTICLES
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="pt-40 pb-32">
      <article className="max-w-3xl mx-auto px-6">
        <Link to="/blog" data-testid="post-back-link" className="inline-flex items-center gap-2 text-ink/50 text-[10px] tracking-[0.24em] font-medium mb-8 hover:text-terra transition-colors duration-200">
          <ArrowLeft size={13} /> ALL ARTICLES
        </Link>
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] tracking-[0.18em] text-ink/40 font-medium mb-6">
          <span className="text-terra">{post.category.toUpperCase()}</span>
          <span className="flex items-center gap-1.5"><Calendar size={11} /> {post.date.toUpperCase()}</span>
          <span className="flex items-center gap-1.5"><Clock size={11} /> {post.read.toUpperCase()} READ</span>
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading font-extrabold tracking-tighter leading-[1.02] text-4xl sm:text-5xl text-ink"
          data-testid="post-title"
        >
          {post.title}
        </motion.h1>
        <div className="mt-7">
          <ShareButtons post={post} />
        </div>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.15 }}
          className="my-10 overflow-hidden aspect-[16/9] bg-stone"
        >
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </motion.div>
        <div className="space-y-6" data-testid="post-body">
          {post.body.map((para, i) => (
            <p key={i} className={`leading-relaxed text-ink/70 ${i === 0 ? 'font-serif text-xl sm:text-2xl text-ink' : 'text-base'}`}>
              {para}
            </p>
          ))}
        </div>
        <div className="mt-14 bg-moss text-sand p-8 lg:p-10" data-testid="post-cta">
          <p className="font-heading font-bold text-xl tracking-tight">Planning to buy a plot?</p>
          <p className="mt-2 text-sm text-sand/60">Get clear titles, verified approvals and an easy EMI facility.</p>
          <button onClick={() => open()} data-testid="post-enquire-btn"
            className="mt-6 bg-terra text-white text-[11px] font-medium tracking-[0.2em] px-8 py-4 flex items-center gap-2 hover:bg-sand hover:text-ink transition-colors duration-300">
            TALK TO OUR TEAM <ArrowUpRight size={14} />
          </button>
        </div>
      </article>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-24">
        <h2 className="font-heading font-extrabold tracking-tighter text-2xl sm:text-3xl text-ink mb-10">Keep reading</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {others.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`related-post-${p.slug}`} className="group">
              <div className="overflow-hidden aspect-[16/10] bg-stone">
                <img src={p.image} alt={p.title} loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
              </div>
              <h3 className="mt-4 font-heading font-bold text-base tracking-tight text-ink group-hover:text-terra transition-colors duration-300 leading-snug">{p.title}</h3>
              <p className="mt-1 text-[10px] tracking-[0.18em] text-ink/40 font-medium">{p.category.toUpperCase()} · {p.read.toUpperCase()}</p>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
